<template>
  <section
    :class="{
      'm-toggle--is-disabled': disabled,
    }"
    class="m-toggle">
    <input
      :id="id"
      v-model="toggleState"
      :disabled="disabled"
      :name="name"
      class="m-toggle__input"
      type="checkbox" />
    <span
      :aria-checked="toggleState"
      :aria-disabled="disabled"
      :aria-labelledby="`${id}-label`"
      :aria-readonly="disabled"
      :style="toggleState && { 'background-color': colorInternal }"
      class="m-toggle__content"
      role="checkbox"
      @click="toggle" />
    <label
      :id="`${id}-label`"
      :for="id"
      :style="{ 'font-size': fontSize, 'font-weight': fontWeight }"
      class="m-toggle__label" />
  </section>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';

  import Colors from '@/Colors';
  import { generateUUID } from '@/globals';

  // Setup
  interface IProps {
    value: boolean;
    color?: string;
    disabled?: boolean;
    fontSize?: string;
    fontWeight?: string;
    name?: string;
  }

  const props = withDefaults(defineProps<IProps>(), {
    disabled: false,
    color: Colors.success,
    fontSize: '16px',
    fontWeight: 'normal',
    name: `toggle-button-${generateUUID()}`,
  });

  interface IEmits {
    (event: 'change', value: boolean): void;
  }

  const emits = defineEmits<IEmits>();

  // Reactive Data
  const toggleState = ref(props.value);
  const colorInternal = ref(props.color ?? Colors.success);

  // Computed
  const id = computed(() => props.name.replace(/ /g, '').toLowerCase());

  // Functions
  function toggle() {
    if (props.disabled) return;
    toggleState.value = !toggleState.value;
    emits('change', toggleState.value);
  }

  // Watchers
  watch(
    () => props.color,
    (value) => {
      colorInternal.value = value;
    },
  );
  watch(
    () => props.value,
    (value) => {
      toggleState.value = value;
    },
  );
</script>

<style scoped>
  .m-toggle {
    align-items: center;
    display: flex;
    margin: 0 -5px;
  }

  .m-toggle > * {
    cursor: pointer;
    margin: 0 5px;
  }

  .m-toggle__label {
    user-select: none;
  }

  .m-toggle--is-disabled .m-toggle__label {
    cursor: not-allowed;
  }

  .m-toggle__input {
    display: none;
  }

  .m-toggle__input:checked + .m-toggle__content:after {
    left: calc(50% + 2px);
  }

  .m-toggle__content {
    background: #bfcbd9;
    border-radius: 2.4rem;
    box-sizing: border-box;
    height: 2.4rem;
    outline: 0;
    overflow: hidden;
    padding: 2px;
    transition: background-color 0.4s ease;
    width: 4.8rem;
    will-change: background-color;
  }

  .m-toggle__content:after {
    background: white;
    border-radius: 50%;
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.05);
    content: '';
    display: block;
    height: 100%;
    left: 0;
    position: relative;
    transition: left 0.2s ease;
    width: calc(50% - 2px);
    will-change: left;
  }

  .m-toggle--is-disabled .m-toggle__content {
    cursor: not-allowed;
    opacity: 50%;
  }
</style>
